import { createSelector } from '@ngrx/store';
import { PAGE_NAME } from 'globals/store/permissions.data';
import { JoiEvent } from '../../../../globals/store/events.data';
import { BACKUP_GROUP, BUDGET_GROUP, PageGroup, PageGroupHandle } from "../../../../globals/store/permissions.data";
import { PermissionAccess } from 'globals/store/permissions.data';
import { programWorkforceReselector } from "./workforce.reselector";
import { WorkerWithProfile } from "../../../../globals/store/workforce.data";
import { canViewBudget } from "../../../../globals/store/budget.data";
import { hasProgramPermissions } from "../../../../globals/store/programs.data";
import { canDoBackups } from "../../../../globals/store/backup.data";
import { eventStateSelector } from '../events.era';
import { userStateSelector } from '../user.era';
import { permissionsStateSelector } from '../permissions.era';
import { accountStateSelector } from '../accounts.era';
import { routerStateSelector } from '../router.state';
import { getSet, jsonCopyObject, makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';


const event = createSelector(eventStateSelector, (state) => state.event)
const account = createSelector(accountStateSelector, (state) => state.account)
const editPermissions = createSelector(permissionsStateSelector, (state) => state.edit)
const addRestrictions = createSelector(permissionsStateSelector, (state) => state.addRestrictions)
const readOnlyPermissions = createSelector(permissionsStateSelector, (state) => state.readonly)
const accessPermissions = createSelector(permissionsStateSelector, (state) => state.access)
const allPermissions = createSelector(permissionsStateSelector, (state) => state.all);
const openPage = createSelector(permissionsStateSelector, (state) => state.openPage)
const groups = createSelector(permissionsStateSelector, (state) => state.groups)
const routerState = createSelector(routerStateSelector, (state) => state?.state)
const user = createSelector(userStateSelector, (state) => state.user);
const userId = createSelector(userStateSelector, (state) => state.user?.uid || null);

export const showEventOptionsDashboardMenuReselector = createSelector( // These boolean checks come DIRECTLY from event-header.component's #eventOptions mat-menu, minus the readonly checks.  To keep it working correctly, this needs to be updated whenever that is updated.
  event,
  account,
  readOnlyPermissions,
  (event, account, readOnlyPermissions) => {
    if (!event || !account || !readOnlyPermissions || !account.is_manager) return false
    const readonly = readOnlyPermissions[PAGE_NAME.program_dashboard] || event.is_readonly
    return ( !event.closed && !event.can_access) // grantWorkforcePrivileges
    || ( !readonly && !event.closed && event.can_access) // editEventTitle
    || ( !readonly && !event.closed && event.can_access) // addLabel
    || ( event.can_access) // closeEvent
    || event.can_access // deleteEvent
}
)

export const isGuestReselector = createSelector(
  accessPermissions,
  (access: PermissionAccess) => {
    return access?.types?.length === 1 && access.types[0] === 'crew'
  }
)

export const eventPermissionsReselector = createSelector(
  event,
  allPermissions,
  (event, permissions) => !event || !allPermissions ? null : { event, permissions }
)


export const ossCanManageProgramReselector = createSelector(
  event,
  accessPermissions,
  (event, { types }) => event && !event.closed && (types.includes('program') || types.includes('logistics'))
)

export const hasProgramPermissionsReselector = createSelector(
  event,
  userId,
  accessPermissions,
  hasProgramPermissions
)

export const readOnlyReselector = createSelector(
  event,
  editPermissions,
  openPage,
  (event, editPermissions, openPage) => {
    const readOnly = !event || event.is_readonly || !editPermissions || !openPage || !editPermissions.includes(openPage.handle)
    // console.log({ event, editPermissons: editPermissions, openPage, readOnly })
    return readOnly
  }
)

export const canAddReselector = createSelector(
  readOnlyReselector,
  addRestrictions,
  openPage,
  (readOnly, addRestrictions, openPage) => {
    return !readOnly && !addRestrictions.includes(openPage.handle)
  }
)

export const accessTypeReselector = createSelector(
  accessPermissions,
  (access) => !access ? null : access.types[0]
)

const allowedSuspendedBaseRoutes = ['dashboard', 'home', 'accounts', 'notification-settings', '/tasks'] // tasks here refers to all tasks.  

export const routeIsSuspendedReselector = createSelector(
  event,
  routerState,
  (event: JoiEvent, routerState) => {
    if (!event || !event.is_suspended || !routerState) return
    const url = routerState.url
    const routeIsSuspended = !allowedSuspendedBaseRoutes.some(baseRoute => url.includes(baseRoute))

    if (routeIsSuspended) {
      console.log(`🦀Router is suspended for this url🦀\n${url}`)
    }
    return routeIsSuspended ? ['event', 'dashboard', event.eventId ] : null
  }
)


export const showBudgetReselector = createSelector(
  event,
  account,
  user,
  hasProgramPermissionsReselector,
  (event: JoiEvent, account, user, hasProgramPermissions) => {
    if (!account || !event || !user || event.accountId !== account.id) return null
    if (canViewBudget(event, account, user, hasProgramPermissions)) return { account, event }
    return null
  }
)

export const canDoBackupsReselector = createSelector(
  event,
  account,
  user,
  (event: JoiEvent, account, user) => {
    if (!account || !event || !user || event.accountId !== account.id) return null
    if (canDoBackups(event, account, user)) return { account, event }
    return null
  }
)

// Much easier to gate editing users behind event owner or account manager than deal with fallout for removing self from budget
export const canEditBudgetUsersReselector = createSelector(
  event,
  account,
  user,
  (event, account, user) => {
    if (!account || !event || !user) return false
    return account.is_manager || event.owner_uid === user.uid
  }
)

export const automaticallyOnBudgetAdminIdsReselector = createSelector(
  event,
  account,
  (event, account) => {
    const automaticallyOnBudgetAdminIds = getSet(event.workforce.filter(wf => wf.uid !== event.owner_uid && 
      account?.admin?.includes(wf.uid) || account?.executive?.includes(wf.uid)), 'uid')
    // console.log({ automaticallyOnBudgetAdminIds, account })
    return automaticallyOnBudgetAdminIds
  }
)

export const potentialBudgetWorkforceReselector = createSelector(
  programWorkforceReselector,
  automaticallyOnBudgetAdminIdsReselector,
  (workforce, automaticallyOnBudgetAdminIds) => {
    const programWorkforce = jsonCopyObject(workforce.filter(w => !automaticallyOnBudgetAdminIds.has(w.uid))) as WorkerWithProfile[]
    const programWorkforceEmails = getSet(workforce, 'email')
    programWorkforce.forEach(w => {
      const { email, firstName: f, lastName: l } = w
      w.name = `${f || ''}${f && l ? ' ' : ''}${l || ''}`
      w.id = email
    })

    return { programWorkforce, programWorkforceEmails }
  }
)




export const groupsByHandleReselector = createSelector(
  groups,
  showBudgetReselector,
  canDoBackupsReselector,
  (groups, showBudgetData, canDoBackupsData): { [K in PageGroupHandle]: PageGroup } => {
    let groupsByHandle = {} as { [K in PageGroupHandle]: PageGroup }
    if (!groups) return groupsByHandle

    groupsByHandle = makeMapFromListOfObjectsAndKey(groups, 'handle') as any

    // console.log({ showBudget })
    if (!!showBudgetData) groupsByHandle.budget = BUDGET_GROUP
    else delete groupsByHandle.budget

    if (!!canDoBackupsData) groupsByHandle.backup = BACKUP_GROUP
    else delete groupsByHandle.backup

    return groupsByHandle  as { [K in PageGroupHandle]: PageGroup }
  }
)
